import MapView from "@arcgis/core/views/MapView";
import Basemap from "@arcgis/core/Basemap";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import { LayerType } from "../../types/layer-type";
import {
  clearSelectedGraphic,
  extentEstateEn,
  focusOnFeature,
  getLayers,
  initializeMap,
  polylineOff,
  polylineOn,
  setConvertedFeatures,
  takeScreenshotAsyncAction,
  zoomIn,
  zoomOut,
} from "../actions/map";
import {
  MapSection,
  masterPlanDataTypes,
  setAttributesModalSelectedFeature,
  setEnableDrawingTools,
  setLayersVisible,
  setLoading,
  setMapComponentReady,
  startDrawingTools,
  stopDrawingTools,
  validArcgisLayerTypes,
} from "../reducers/map";
import createViewer, {
  applyHoverEffect,
  changeBaseMap,
  CreateHybridBaseMap,
  finishDrawingProcess,
  getPopUpTemplate,
  handleExtentEstate,
  handleFocusOnFeature,
  resetHoverEffects,
  startDrawingProcess,
  stopMeasuring,
  unFocus,
  updateSnappingSources,
  waitForViewerReady,
} from "../../utils/helpers-for-2d-map";
import { LayersDataTypes } from "../../types/layers-data-types";
import { Middleware } from "redux";
import { RootState } from "../store";
import { ArcgisHybridLayer, Layer } from "../../interfaces/layer";
import PopupTemplate from "@arcgis/core/PopupTemplate";
import {
  getGeometryForContour,
  setEditingBorders,
  setLayerVisibilityByEstateEn,
} from "../actions/master-plan-modification";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Graphic from "@arcgis/core/Graphic";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import SimpleRenderer from "@arcgis/core/renderers/SimpleRenderer";
import TextSymbol from "@arcgis/core/symbols/TextSymbol";
import Color from "@arcgis/core/Color";
import Point from "@arcgis/core/geometry/Point";
import ArcgisMap from "@arcgis/core/Map";
import SketchViewModel from "@arcgis/core/widgets/Sketch/SketchViewModel";
import {
  FormationTypeNames,
  setGeometries,
  setScreenshot,
} from "../reducers/master-plan-modification";
import Polygon from "@arcgis/core/geometry/Polygon";
import { v4 as uuidv4 } from "uuid";
import {
  ConvertPolygonsToCoordinatesTable,
  ConvertToPolygons,
  isPolygon,
} from "../../utils/master-plan-helper";
import {
  addConvertedFeaturesToLayer,
  applyEditToPlotGeometry,
  buildDefinitionExpression,
  buildVertexIndexMap,
  clearLastSelectedGraphic,
  DrawVertices,
  DrawVerticesLabels,
  EditGraphics,
  EndCreateGraphic,
  getGeometry,
  handleDeleteKey,
  handleFeatureClickStyle,
  handleViewerClick,
} from "../../utils/master-map-helper";
import Polyline from "@arcgis/core/geometry/Polyline";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import PrintTemplate from "@arcgis/core/rest/support/PrintTemplate";
import PrintParameters from "@arcgis/core/rest/support/PrintParameters";
import * as print from "@arcgis/core/rest/print";
import { FieldInfo } from "../../interfaces/krookie";
import { ensureIdentityTokenForResource } from "../../utils/arcgis-auth";

// Neighboring plots' geometry (see takeScreenshotAsyncAction below) comes
// back from the server at full precision — many decimal places, sub-
// millimetre — which is meaningless for a printed map but bloats the
// Web_Map_as_JSON payload sent to the print/export service. A dense area
// with many neighbors was large enough to get the export request rejected
// outright (HTTP 413, Content Too Large). Rounding to roughly centimetre
// precision (degrees for a geographic SR, meters otherwise — these layers
// are UTM/meters in practice, but matching the pattern used elsewhere in
// this file for the corner-label inset) keeps the print visually identical
// while cutting that payload down.
const roundPolygonPrecision = (polygon: Polygon): Polygon => {
  const decimals = polygon.spatialReference?.isGeographic ? 7 : 2;
  const factor = 10 ** decimals;
  return new Polygon({
    rings: polygon.rings.map((ring) =>
      ring.map((point) => point.map((coord) => Math.round(coord * factor) / factor)),
    ),
    spatialReference: polygon.spatialReference,
  });
};

export const masterPlanMiddleware = (
  section: MapSection,
): Middleware<{}, RootState> => {
  return (store) => {
    let viewer: MapView | null = null;
    let baseMap: Basemap;
    let tempLayer: GraphicsLayer;
    let linesLayer: GraphicsLayer;
    let verticesTempLayer: GraphicsLayer;
    let verticesLabelTempLayer: GraphicsLayer;
    let loadedObjectsLayer: GraphicsLayer;
    let polygonsLayer: GraphicsLayer;
    let staticGeometryLayer: GraphicsLayer;
    let sketchViewModel: SketchViewModel;
    const { dispatch } = store;

    const polygonTempSymbol = new SimpleFillSymbol({
      color: "#2E1A4759",
      outline: { color: "#2E1A47CC", width: 3 },
    });

    // Used only by the isolated print view (see takeScreenshotAsyncAction
    // below) — never applied to the live/interactive viewer or its layers.
    // Matches the brand's dark red ($red-100 in _colors.scss / the logo).
    const printBrandRedColor = "#B04A5A";
    const printPlainRenderer = new SimpleRenderer({
      symbol: new SimpleFillSymbol({
        color: [0, 0, 0, 0],
        outline: { color: printBrandRedColor, width: 1 },
      }),
    });
    const printHighlightSymbol = new SimpleFillSymbol({
      color: [0, 0, 0, 0],
      outline: { color: "#2E1A47", width: 3 },
    });

    function AddLayers(viewer: MapView) {
      const { layers, visibleLayersId } = store.getState().map;

      const baseMapLayer = layers.find(
        (l) =>
          l.dataType === LayersDataTypes.BaseMap &&
          visibleLayersId.includes(l.id),
      );
      if (baseMapLayer && baseMapLayer !== ArcgisHybridLayer) {
        changeBaseMap(baseMapLayer, viewer, getBaseMap);
      }
      const allVisible = [
        ...visibleLayersId,
        ...layers
          .filter(
            (item) =>
              !item.parentId &&
              !item.hidden &&
              !visibleLayersId.includes(item.id) &&
              validArcgisLayerTypes.includes(item.type) &&
              (item.dataType === LayersDataTypes.Plots ||
                item.dataType === LayersDataTypes.InvestmentOpportunities),
          )
          .map((item) => item.id),
      ];
      dispatch(setLayersVisible(allVisible));
      layers
        .filter(
          (layer) =>
            masterPlanDataTypes.includes(layer.dataType) &&
            layer.type === LayerType.ArcgisFeatureServer,
        )
        .forEach((layer) => {
          if (layer.dataType === LayersDataTypes.BaseMap) return;
          const visible =
            visibleLayersId.includes(layer.id) ||
            (!!layer.parentId && visibleLayersId.includes(layer.parentId));
          const layerProperties = {
            title: layer.label,
            id: layer.name,
            url: layer.url,
            visible: visible,
            editingEnabled: layer.canEdit ?? false,
            //TODO temporary crutch
            definitionExpression: layer.style2D?.query?.replace(
              " and LandUse <> 3",
              "",
            ),
            outFields: ["*"],
            ...(layer.style2D?.label?.type === "onclick" &&
              layer.style2D?.label && {
                popupTemplate: new PopupTemplate(
                  getPopUpTemplate(layer.style2D?.label),
                ),
              }),
          };
          if (layer.style2D?.default) {
            layerProperties.renderer = layer.style2D?.default;
          }
          const arcgisLayer = new FeatureLayer(layerProperties);

          let hoverStyle = layer.style2D?.hover;
          const isHover = layer.style2D?.label?.type === "hover";
          const mode = (hoverStyle ? "style" : "") + (isHover ? "popup" : "");

          if (mode) {
            if (isHover && layer.style2D?.label) {
              arcgisLayer.popupTemplate = new PopupTemplate(
                getPopUpTemplate(layer.style2D?.label),
              );
            }
            applyHoverEffect(
              viewer,
              arcgisLayer,
              mode,
              hoverStyle,
              layer.style2D?.fieldName,
            );
          }

          viewer.map.add(arcgisLayer);
        });
    }
    function getBaseMap(layer: Layer) {
      return layer === ArcgisHybridLayer ? baseMap : null;
    }
    function DrawTempGeometry(enableClearSelectedGraphic: boolean = false) {
      if (sketchViewModel.state === "active") {
        sketchViewModel.complete();
      }

      const polygonsAndLinesIds = [
        ...polygonsLayer.graphics,
        ...linesLayer.graphics,
      ].map((g) => g.attributes.uuid);

      verticesTempLayer.removeMany(
        verticesTempLayer.graphics
          .filter((g) => polygonsAndLinesIds.includes(g.attributes.uuid))
          .toArray(),
      );
      verticesLabelTempLayer.removeMany(
        verticesLabelTempLayer.graphics
          .filter((g) => polygonsAndLinesIds.includes(g.attributes.uuid))
          .toArray(),
      );
      polygonsLayer.removeAll();
      linesLayer.removeAll();

      let lastGraphic: Graphic | null = null;

      store
        .getState()
        .masterPlanModification.krookiePlots.filter((plot) => plot.newGeometry)
        .forEach((plot) => {
          if (!plot.newGeometry) return;

          const spatialReference = plot.newGeometry.spatialReference;
          const allRings = plot.newGeometry.rings || [];

          let coordIndexMap: Map<string, number> = new Map();

          const closedRingsWithIndex: { ring: number[][]; index: number }[] =
            [];
          const openPaths: { path: number[][]; index: number }[] = [];

          coordIndexMap = buildVertexIndexMap(allRings);

          const uuid = uuidv4();

          allRings.forEach((ring, index) => {
            if (isPolygon(ring)) {
              closedRingsWithIndex.push({ ring, index });
            } else {
              openPaths.push({ path: ring, index });
            }
          });

          if (closedRingsWithIndex.length) {
            const closedRings = closedRingsWithIndex.map((i) => i.ring);
            const closedRingIndices = closedRingsWithIndex.map((i) => i.index);

            const polygon = new Polygon({
              rings: closedRings,
              spatialReference,
            });

            const polygonGraphic = new Graphic({
              geometry: polygon,
              symbol: polygonTempSymbol,
              attributes: {
                objectId: plot.objectId,
                uuid,
                spatialReference,
                ringIndices: closedRingIndices,
              },
            });

            polygonsLayer.add(polygonGraphic);
            lastGraphic = polygonGraphic;

            DrawVertices(polygon, uuid, false, verticesTempLayer);
            DrawVerticesLabels(
              polygon,
              uuid,
              false,
              verticesLabelTempLayer,
              coordIndexMap,
            );
          }

          openPaths.forEach(({ path, index }) => {
            const polyline = new Polyline({ paths: [path], spatialReference });

            const lineGraphic = new Graphic({
              geometry: polyline,
              symbol: new SimpleLineSymbol({ color: "#2E1A47CC", width: 2 }),
              attributes: {
                objectId: plot.objectId,
                uuid,
                spatialReference,
                ringIndex: index,
              },
            });

            linesLayer.add(lineGraphic);
            lastGraphic = lineGraphic;

            DrawVertices(polyline, uuid, false, verticesTempLayer);
            DrawVerticesLabels(
              polyline,
              uuid,
              false,
              verticesLabelTempLayer,
              coordIndexMap,
            );
          });
        });

      if (lastGraphic && !enableClearSelectedGraphic) {
        const graphic = lastGraphic as Graphic;
        const targetLayer =
          graphic.geometry.type === "polyline" ? linesLayer : polygonsLayer;
        handleFeatureClickStyle(
          graphic,
          store,
          targetLayer,
          viewer,
          sketchViewModel,
        );
      } else {
        clearSelection();
      }
    }

    function DrawStaticGeometry() {
      staticGeometryLayer.removeAll();

      const selectedPlot =
        store.getState().masterPlanModification.selectedPlots[0];

      if (!selectedPlot.geometry) return;

      const plotGeometry = selectedPlot.geometry;

      const staticGraphic = new Graphic({
        geometry: plotGeometry,
        symbol: new SimpleFillSymbol({
          color: "#FAFAFA33",
          outline: {
            color: "#FFFFFF",
            width: 3,
          },
        }),
      });

      staticGeometryLayer.add(staticGraphic);
    }

    function updateSnappingWrapper() {
      const ignoredLayers = [verticesTempLayer, verticesLabelTempLayer];

      updateSnappingSources(viewer, sketchViewModel, ignoredLayers);
    }

    function clearSelection() {
      clearLastSelectedGraphic([
        loadedObjectsLayer,
        linesLayer,
        tempLayer,
        polygonsLayer,
      ]);
      dispatch(setAttributesModalSelectedFeature(null));
    }

function moveGraphicsLayersToTop() {
      const graphicsLayers = [
        loadedObjectsLayer,
        staticGeometryLayer,
        polygonsLayer,
        linesLayer,
        tempLayer,
        verticesTempLayer,
        verticesLabelTempLayer,
      ];

      graphicsLayers.forEach((layer) => {
        if (viewer && viewer.map.layers.includes(layer)) {
          viewer.map.reorder(layer, viewer.map.layers.length);
        }
      });
    }

    return (next) => async (action) => {
      if (initializeMap.match(action)) {
        if (action.payload.section === section) {
          if (viewer === null) {
            await next(action);

            baseMap = CreateHybridBaseMap();
            viewer = createViewer(
              store,
              AddLayers,
              `${section}MapContainer`,
              baseMap,
            );

            tempLayer = new GraphicsLayer();
            verticesTempLayer = new GraphicsLayer();
            verticesLabelTempLayer = new GraphicsLayer();
            linesLayer = new GraphicsLayer();
            loadedObjectsLayer = new GraphicsLayer();
            polygonsLayer = new GraphicsLayer();
            staticGeometryLayer = new GraphicsLayer();

            viewer.map.add(loadedObjectsLayer);
            viewer.map.add(staticGeometryLayer);
            viewer.map.add(polygonsLayer);
            viewer.map.add(linesLayer);
            viewer.map.add(tempLayer);
            viewer.map.add(verticesTempLayer);
            viewer.map.add(verticesLabelTempLayer);

            sketchViewModel = new SketchViewModel({
              updateOnGraphicClick: false,
              defaultUpdateOptions: {
                tool: "reshape",
                toggleToolOnClick: false,
                reshapeOptions: {
                  shapeOperation: "none",
                },
              },
              layer: tempLayer,
              view: viewer,
              snappingOptions: {
                enabled: true,
                selfEnabled: false,
                featureSources: [],
              },
              polygonSymbol: new SimpleFillSymbol({
                color: "#FAFAFA80",
                outline: {
                  color: "#E59E6D",
                  width: 3,
                },
              }),
            });

            sketchViewModel.on("update", (event) => {
              if (event.toolEventInfo) {
                (async () => {
                  const mergedGeometryGraphic = await applyEditToPlotGeometry(
                    event.graphics[0],
                    store.getState().masterPlanModification.krookiePlots,
                  );
                  if (mergedGeometryGraphic) {
                    const rings = (mergedGeometryGraphic!.geometry as Polygon)
                      .rings;
                    const coordIndexMap = buildVertexIndexMap(rings);
                    EditGraphics(
                      [mergedGeometryGraphic],
                      dispatch,
                      verticesLabelTempLayer,
                      undefined,
                      coordIndexMap,
                    );
                  }
                })();
              } else {
                if (event.state === "start" || event.state === "complete") {
                  event.graphics.forEach((graphic) => {
                    DrawVertices(
                      graphic.geometry as Polygon,
                      graphic.attributes.uuid,
                      event.state === "start",
                      verticesTempLayer,
                    );
                  });
                }
              }
            });
            sketchViewModel.on("create", (event) => {
              if (event.state === "cancel" && !event.graphic?.attributes) {
                return;
              }
              if (event.state === "start") {
                event.graphic.attributes = { uuid: uuidv4() };
              }
              if (event.state === "complete") {
                DrawVertices(
                  event.graphic.geometry as Polygon,
                  event.graphic.attributes.uuid,
                  false,
                  verticesTempLayer,
                );

                handleFeatureClickStyle(
                  event.graphic,
                  store,
                  [tempLayer, linesLayer, polygonsLayer],
                  viewer,
                  sketchViewModel,
                );
              }
              if (event.graphic.attributes.objectId) {
                DrawVerticesLabels(
                  event.graphic.geometry as Polygon,
                  event.graphic.attributes.uuid,
                  event.state === "cancel",
                  verticesLabelTempLayer,
                );
              }
            });

            viewer!.on("click", async (event) => {
              const response = await viewer!.hitTest(event);

              const clickedGraphicResult = response.results[0];

              handleViewerClick(
                event,
                store,
                sketchViewModel,
                viewer,
                [tempLayer, linesLayer, loadedObjectsLayer, polygonsLayer],
                tempLayer,
                loadedObjectsLayer,
              );

              if (clickedGraphicResult && "graphic" in clickedGraphicResult) {
                const graphic = clickedGraphicResult.graphic;

                const isLoadedObjectsLayer =
                  graphic.layer === loadedObjectsLayer;
                const isFeatureLayer = graphic.layer instanceof FeatureLayer;

                let fieldsInfo: FieldInfo[] | null = null;
                let layerUrl: string | null = null;

                if (isLoadedObjectsLayer || isFeatureLayer) {
                  if (isFeatureLayer) {
                    const layerId = graphic.layer.id;
                    const layers = store
                      .getState()
                      .map.layers.filter((l) => l.name === layerId);
                    const isAllowed = layers.some(
                      (l) =>
                        l.dataType === LayersDataTypes.Plots ||
                        l.dataType === LayersDataTypes.InvestmentOpportunities,
                    );
                    const featureLayer = graphic.layer as FeatureLayer;
                    fieldsInfo = featureLayer.fields.map((field) => {
                      let codedValueDict: Record<string, string> | null = null;
                      if (field.domain && field.domain.type === "coded-value") {
                        codedValueDict = {};
                        field.domain.codedValues.forEach((cv) => {
                          codedValueDict![cv.code] = cv.name;
                        });
                      }
                      return {
                        fieldName: field.name,
                        type: field.type,
                        editable: field.editable,
                        codedValueDict: codedValueDict,
                        nullable: field.nullable,
                        defaultValue: field.defaultValue,
                        maxLength: field.length,
                      };
                    });
                    layerUrl = `${featureLayer.url}/${featureLayer.layerId}`;

                    if (!isAllowed) return;
                  }

                  dispatch(
                    setAttributesModalSelectedFeature({
                      layerName:
                        graphic.layer instanceof FeatureLayer
                          ? graphic.layer.title
                          : (graphic as any).layerName,
                      attributes: graphic.attributes,
                      fieldsInfo,
                      layerUrl,
                    }),
                  );
                  return;
                }
              } else {
                clearSelection();
              }
            });

            await waitForViewerReady(viewer);

            viewer.container.addEventListener("keydown", (event) => {
              const krookePlots =
                store.getState().masterPlanModification.krookiePlots;

              if (event.key === "Escape") {
                clearSelection();
                return;
              }

              handleDeleteKey(
                event,
                store,
                [tempLayer, linesLayer, polygonsLayer],
                verticesTempLayer,
                krookePlots,
                () =>
                  store.dispatch(
                    setGeometries({
                      newGeometry: ConvertToPolygons([]),
                      stringGeometry: ConvertPolygonsToCoordinatesTable([]),
                    }),
                  ),
                verticesLabelTempLayer,
                false,
                sketchViewModel,
              );
            });
            dispatch(setMapComponentReady(section));
            updateSnappingWrapper();
            moveGraphicsLayersToTop();
          }
          return;
        } else if (viewer !== null) {
          unFocus();
          viewer.map?.removeAll();
          viewer?.destroy();
          viewer = null;
          dispatch(setMapComponentReady(MapSection.none));
        }
      }
      if (viewer === null || store.getState().map.section !== section) {
        return next(action);
      }
      if (setEditingBorders.match(action)) {
        const mode = action.payload;
        const shouldReset = mode === "reset" || mode === "resetAndDraw";
        const shouldDraw = mode === "draw" || mode === "resetAndDraw";

        const plots = store.getState().masterPlanModification.selectedPlots;
        const krookieFormationTypeName =
          store.getState().masterPlanModification.selectedFormationType;
        const selectedCity =
          store.getState().masterPlanModification.selectedCity;

        const processedLayers = new Set();

        for (const plot of plots) {
          const hasPlotLayer =
            plot && plot.layer && Object.keys(plot.layer).length > 0;

          if (hasPlotLayer && !processedLayers.has(plot.layer.name)) {
            processedLayers.add(plot.layer.name);

            const layer = viewer?.map.findLayerById(
              plot.layer.name,
            ) as FeatureLayer;

            if (!layer) {
              continue;
            }

            if (!layer.loaded) {
              try {
                await layer.load();
              } catch (error) {
                throw error;
              }
            }

            const objectIdField = layer.objectIdField;
            const objectIds = plots
              .filter((p) => p.layer.name === plot.layer.name)
              .map((p) => p.objectId);

            const filteredQuery = (plot.layer.style2D?.query || "1=1").replace(
              " and LandUse <> 3",
              "",
            );

            if (shouldReset) {
              layer.definitionExpression = filteredQuery;
            }

            if (shouldDraw && selectedCity) {
              layer.definitionExpression = buildDefinitionExpression({
                estateIds: [selectedCity],
                objectIds,
                objectIdField,
                baseQuery: filteredQuery,
              });
            }
          }
        }

        if (shouldReset) {
          tempLayer.removeAll();
          polygonsLayer.removeAll();
          linesLayer.removeAll();
          verticesTempLayer.removeAll();
          verticesLabelTempLayer.removeAll();
          staticGeometryLayer.removeAll();
        }

        if (shouldDraw) {
          unFocus();
          dispatch(setEnableDrawingTools(true));

          if (krookieFormationTypeName === FormationTypeNames.Divide) {
            DrawStaticGeometry();
          }
          DrawTempGeometry();
        }
      }

      if (setGeometries.match(action)) {
        const { clearSelection } = action.payload;
        next(action);
        DrawTempGeometry(!!clearSelection);
        return;
      }

      if (startDrawingTools.match(action)) {
        tempLayer.graphics.forEach((graphic) => {
          DrawVertices(
            graphic.geometry as Polygon,
            graphic.attributes.uuid,
            false,
            verticesTempLayer,
          );
        });
      }

      if (stopDrawingTools.match(action)) {
        if (
          sketchViewModel.state === "active" &&
          sketchViewModel.createGraphic
        ) {
          EndCreateGraphic(sketchViewModel);
        } else {
          sketchViewModel.complete();
        }
        const uuids = tempLayer.graphics.map(
          (graphic) => graphic.attributes.uuid,
        );
        verticesTempLayer.removeMany(
          verticesTempLayer.graphics
            .filter((g) => uuids.includes(g.attributes.uuid))
            .toArray(),
        );
      }

      if (takeScreenshotAsyncAction.match(action)) {
        const { data, deferred } = action.payload;

        resetHoverEffects(viewer);

        tempLayer.removeAll();
        DrawTempGeometry(true);

        const layerVisibilityToRestore = new Map<GraphicsLayer, boolean>([
          [staticGeometryLayer, staticGeometryLayer.visible],
          [loadedObjectsLayer, loadedObjectsLayer.visible],
        ]);

        staticGeometryLayer.visible = false;
        loadedObjectsLayer.visible = false;

        const printServiceUrl =
          "https://gis.madayn.om/server/rest/services/Utilities/PrintingTools/GPServer/Export%20Web%20Map%20Task";

        // Screenshots are captured on a separate, isolated MapView — no
        // basemap, plot layers styled with a plain black outline — so the
        // live/interactive viewer and its WebGL context are never touched.
        let printView: MapView | undefined;
        let printContainer: HTMLDivElement | undefined;

        try {
          await ensureIdentityTokenForResource(store, printServiceUrl);

          const plotsLayerConfigs = store
            .getState()
            .map.layers.filter(
              (l) => l.dataType === LayersDataTypes.Plots && !!l.url,
            );

          const printPlotsLayers = plotsLayerConfigs.map(
            (l) =>
              new FeatureLayer({
                url: l.url!,
                renderer: printPlainRenderer,
                // The service's own published scale range hides plots
                // once zoomed out past its configured minScale — fine for
                // the live map, but our far screenshot scale (now up to
                // 1:20000 for large plots) can exceed it, leaving
                // neighboring plots missing from that screenshot. 0/0
                // disables the scale-dependent visibility for this
                // print-only layer so plots always render regardless of
                // the print view's scale.
                minScale: 0,
                maxScale: 0,
                // Match AddLayers() (the live view) exactly, including
                // definitionExpression this time: one service (Plots_39)
                // reliably fails to render through this print path — but
                // not through the live view, and not because of extent
                // size (production's naturally-fit far screenshot covers a
                // *wider* area than ours and still works) — so the
                // remaining difference is the query itself. The live
                // view's definitionExpression may be excluding rows that
                // break rendering when included unfiltered. Matching it
                // means print only ever shows what the live map already
                // shows — no more "every plot regardless of filters".
                title: l.label,
                id: l.name,
                outFields: ["*"],
                definitionExpression: l.style2D?.query?.replace(
                  " and LandUse <> 3",
                  "",
                ),
              }),
          );

          // The plot-number labels come from each layer's own (service-side)
          // labelingInfo, not from anything we define — recolor them to the
          // brand red without touching the label expression/font/etc.
          await Promise.all(
            printPlotsLayers.map(async (layer) => {
              await layer.load();
              layer.labelingInfo?.forEach((labelClass) => {
                if (labelClass.symbol) {
                  (labelClass.symbol as TextSymbol).color = new Color(
                    printBrandRedColor,
                  );
                }
              });
            }),
          );

          // Base filter per layer (the live view's own definitionExpression,
          // matched above) — remembered so the per-plot exclusion below can
          // be recomputed from scratch each iteration instead of compounding
          // onto whatever the previous item's exclusion left behind.
          const baseDefinitionExpressions = new Map(
            printPlotsLayers.map((layer) => [
              layer,
              layer.definitionExpression ?? "",
            ]),
          );

          const printHighlightLayer = new GraphicsLayer();

          printContainer = document.createElement("div");
          printContainer.style.position = "fixed";
          printContainer.style.top = "-10000px";
          printContainer.style.left = "-10000px";
          printContainer.style.width = "1236px";
          printContainer.style.height = "1133px";
          printContainer.style.pointerEvents = "none";
          document.body.appendChild(printContainer);

          printView = new MapView({
            container: printContainer,
            map: new ArcgisMap({
              layers: [...printPlotsLayers, printHighlightLayer],
            }),
            constraints: { rotationEnabled: false },
          });

          await printView.when();

          for (let item of data) {
            const objectId = item.objectId;

            // An existing plot being edited/approved (or, for Merge/Divide,
            // the original plot(s) this new one replaces — item's own id
            // plus excludeObjectIds) is still on the live layer with its
            // old, unedited geometry (nothing's saved yet) — left in, that
            // live layer draws its own outline and service-side number
            // label right underneath the highlighted (new-geometry)
            // graphics we draw ourselves below, printing the plot(s) twice.
            // Excluding them from the layer itself (rather than trying to
            // filter them back out after the fact) removes them from both
            // that native rendering and our own neighbor query below in one
            // place — the highlighted item's own number then always comes
            // from our own label instead (see the label block further
            // down), same as it already worked for a brand-new Create plot
            // that isn't in the layer yet.
            const idsToExclude = [
              ...(objectId != null ? [objectId] : []),
              ...(item.excludeObjectIds ?? []),
            ];
            printPlotsLayers.forEach((layer) => {
              const base = baseDefinitionExpressions.get(layer) ?? "";
              const exclude =
                idsToExclude.length > 0
                  ? `${layer.objectIdField} NOT IN (${idsToExclude.join(",")})`
                  : "";
              layer.definitionExpression = [base, exclude]
                .filter(Boolean)
                .join(" AND ");
            });

            // Precision only, never generalized/simplified — this is the
            // plot's own official boundary, not a neighbor for visual
            // context, so its shape must stay exact. A large/complex
            // plot's full-precision geometry was itself a meaningful
            // contributor to the print/export request exceeding its size
            // limit (HTTP 413); trimming decimal noise costs nothing.
            const printGeometry =
              item.geometry.type === "polygon"
                ? roundPolygonPrecision(item.geometry as Polygon)
                : item.geometry;

            printHighlightLayer.removeAll();
            printHighlightLayer.add(
              new Graphic({
                geometry: printGeometry,
                symbol: printHighlightSymbol,
              }),
            );

            if (item.fixedScale) {
              await printView.goTo({
                target: item.geometry,
                scale: item.fixedScale,
              });
            } else {
              await printView.goTo({ target: item.geometry });

              if (item.zoomAdjustment) {
                await printView.goTo({
                  target: item.geometry,
                  zoom: printView.zoom + item.zoomAdjustment,
                });
              }
            }

            // Fetch neighboring plot outlines ourselves and draw them as
            // graphics, instead of relying solely on each FeatureLayer's
            // own service-side rendering during export — a single
            // problematic geometry among many neighbors can make that
            // whole layer silently fail to render server-side (seen in
            // practice for one specific area), while a plain client-side
            // query for the same extent tends to still succeed: fetching
            // raw feature data is more tolerant than rendering it.
            // Lightweight (one query per layer per screenshot, geometry
            // only) and each layer is queried independently with its own
            // try/catch, so one broken layer/feature can't blank out the
            // others. The layers stay in the print map too (for their own
            // service-side labeling), so this is a resilience backstop,
            // not a replacement.
            //
            // The highlighted plot itself is excluded from every layer
            // above (by objectId, before this query runs) — but
            // queryFeatures() with a plain query object does NOT
            // automatically inherit the layer's definitionExpression (that
            // only governs the layer's own rendering); it has to be passed
            // explicitly as `where` below, or the excluded plot still comes
            // back in these results despite being correctly filtered out of
            // the live map itself.
            await Promise.all(
              printPlotsLayers.map(async (layer) => {
                try {
                  const result = await layer.queryFeatures({
                    where: layer.definitionExpression,
                    geometry: printView!.extent,
                    spatialRelationship: "intersects",
                    returnGeometry: true,
                    outFields: [layer.objectIdField],
                    // Lets the server drop vertices finer than ~2.5m —
                    // invisible at any of our print scales, but cuts the
                    // vertex count (and so the payload size) for detailed
                    // polygons well beyond what rounding coordinates alone
                    // achieves.
                    maxAllowableOffset: 2.5,
                    // A large plot's far screenshot can cover a wide
                    // enough area (dense industrial zone) that even
                    // generalized/rounded geometry for every neighbor
                    // still made the print/export request too large (HTTP
                    // 413) — even at a 500-per-layer cap and 1m
                    // generalization. Past a hundred or so, more outlines
                    // wouldn't be legible on a small printed map anyway,
                    // so cap it harder — num (resultRecordCount) limits
                    // this server-side, before the data is even sent to
                    // the browser.
                    num: 150,
                  });
                  result.features.forEach((feature) => {
                    const neighborGeometry =
                      feature.geometry?.type === "polygon"
                        ? roundPolygonPrecision(feature.geometry as Polygon)
                        : feature.geometry;
                    printHighlightLayer.add(
                      new Graphic({
                        geometry: neighborGeometry,
                        symbol: printPlainRenderer.symbol,
                      }),
                    );
                  });
                } catch (err) {
                  console.error(
                    "Failed to fetch neighboring plots for print:",
                    err,
                  );
                }
              }),
            );

            // The highlighted plot is always excluded from the live layers
            // above, so its own service-side label never renders — this is
            // the only place its number gets drawn, for both a brand-new
            // Create plot and an existing plot being edited alike.
            if (item.label && item.geometry.type === "polygon") {
              const centroid = (item.geometry as Polygon).centroid;
              if (centroid) {
                printHighlightLayer.add(
                  new Graphic({
                    geometry: centroid,
                    symbol: new TextSymbol({
                      text: item.label,
                      color: "#2E1A47",
                      haloColor: "#FFFFFF",
                      haloSize: 1.5,
                      font: { size: 12, weight: "bold" },
                    }),
                  }),
                );
              }
            }

            // Numbered corner labels (no marker dot), matching the line
            // numbering used by the document's coordinates table
            // (ConvertToLines): a running counter across rings, skipping
            // each ring's closing duplicate. Added after goTo so the label
            // can be pushed a fixed number of screen pixels from the corner
            // *towards the polygon centre* (i.e. inside the plot) using the
            // final view scale. Only drawn on the closer/tighter scale of
            // the plot's pair (item.showCornerLabels) — at the farther
            // scale the plot is small on screen and the labels would be
            // too cramped to read.
            if (item.showCornerLabels && item.geometry.type === "polygon") {
              const polygon = item.geometry as Polygon;
              const center = polygon.centroid;
              const metersPerPixel = (printView.scale * 0.0254) / 96;
              const insetMeters = metersPerPixel * 16;
              const insetUnits = polygon.spatialReference?.isGeographic
                ? insetMeters / 111320
                : insetMeters;
              let cornerNumber = 1;
              for (const ring of polygon.rings) {
                ring.slice(0, -1).forEach((coords) => {
                  const cornerPoint = new Point({
                    x: coords[0],
                    y: coords[1],
                    spatialReference: polygon.spatialReference,
                  });
                  let labelPoint = cornerPoint;
                  if (center) {
                    const dx = center.x - coords[0];
                    const dy = center.y - coords[1];
                    const length = Math.hypot(dx, dy);
                    if (length > 0) {
                      // Never push past the centre on a very small plot.
                      const shift = Math.min(insetUnits, length / 2);
                      labelPoint = new Point({
                        x: coords[0] + (dx / length) * shift,
                        y: coords[1] + (dy / length) * shift,
                        spatialReference: polygon.spatialReference,
                      });
                    }
                  }
                  printHighlightLayer.add(
                    new Graphic({
                      geometry: labelPoint,
                      symbol: new TextSymbol({
                        text: String(cornerNumber),
                        color: "#000000",
                        haloColor: "#FFFFFF",
                        haloSize: 1.5,
                        horizontalAlignment: "center",
                        verticalAlignment: "middle",
                        font: { size: 16, weight: "normal" },
                      }),
                    }),
                  );
                  cornerNumber += 1;
                });
              }
            }

            const watermarkHeight = 300;
            const crop = watermarkHeight / 2;

            const template = new PrintTemplate({
              format: "jpg",
              layout: "map-only",
              scalePreserved: true,
              outScale: item.fixedScale ?? printView.scale,
              exportOptions: {
                width: 1236,
                height: 1133 + watermarkHeight,
                dpi: 300,
              },
            });

            const parameters = new PrintParameters({
              view: printView,
              template,
            });

            const { url } = await print.execute(printServiceUrl, parameters);

            const response = await fetch(url);
            const blob = await response.blob();
            const imageUrl = URL.createObjectURL(blob);

            const img = new Image();
            img.src = imageUrl;

            await new Promise((resolve) => (img.onload = resolve));

            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height - watermarkHeight;

            const ctx = canvas.getContext("2d");

            if (!ctx) {
              deferred.reject(new Error("Failed to get canvas 2D context"));
              return;
            }

            ctx.drawImage(
              img,
              0,
              crop,
              img.width,
              canvas.height,
              0,
              0,
              img.width,
              canvas.height,
            );

            const croppedUrl = canvas.toDataURL("image/jpeg", 1.0);
            URL.revokeObjectURL(imageUrl);

            if (objectId) {
              dispatch(
                setScreenshot({
                  id: objectId,
                  screenshot: {
                    url: croppedUrl,
                    scale: item.fixedScale ?? Math.round(printView.scale),
                  },
                }),
              );
            }
          }

          deferred.resolve([]);
        } catch (err) {
          deferred.reject(err);
        } finally {
          layerVisibilityToRestore.forEach((visible, layer) => {
            layer.visible = visible;
          });

          printView?.destroy();
          printContainer?.remove();
        }
      }

      if (focusOnFeature.match(action)) {
        let { layer, geometry, objectId } = action.payload;
        const plot = store
          .getState()
          .masterPlanModification.krookiePlots.find(
            (p) => p.objectId === objectId,
          );
        if (plot?.newGeometry) {
          geometry = plot.newGeometry;
        }
        handleFocusOnFeature(
          layer,
          geometry,
          objectId,
          dispatch,
          store,
          viewer,
          false,
          false,
        );
      }

      if (setLayersVisible.match(action)) {
        next(action);
        const { layers, visibleLayersId } = store.getState().map;
        layers
          .filter((layer) => masterPlanDataTypes.includes(layer.dataType))
          .forEach((layer) => {
            if (layer.dataType === LayersDataTypes.BaseMap) {
              if (visibleLayersId.includes(layer.id))
                changeBaseMap(layer, viewer, getBaseMap);
            } else if (validArcgisLayerTypes.includes(layer.type)) {
              let mapLayers = viewer!.map.layers.filter(
                (l) => l.id === layer.name,
              );
              if (mapLayers) {
                const visible =
                  visibleLayersId.includes(layer.id) ||
                  (!!layer.parentId &&
                    visibleLayersId.includes(layer.parentId));
                Promise.all(
                  mapLayers.map((mapLayer) => {
                    mapLayer.visible = visible;
                    return mapLayer.load();
                  }),
                ).finally(() => {
                  dispatch(setLoading({ id: layer.id, loading: false }));
                });
              }
            }
          });
        moveGraphicsLayersToTop();
        return;
      }

      if (getGeometryForContour.match(action)) {
        const { plotIdx, contourIdx } = action.payload;
        const coordinatesData =
          store.getState().masterPlanModification.coordinatesData;
        const existingGeometry =
          store.getState().masterPlanModification.krookiePlots?.[0]?.geometry;
        const targetWkid = existingGeometry?.spatialReference?.wkid;

        const result = await getGeometry(
          plotIdx,
          contourIdx,
          targetWkid,
          tempLayer,
          coordinatesData,
        );

        if (!result) return;

        const { uuid, updatedData } = result;

        [tempLayer, verticesTempLayer, verticesLabelTempLayer].forEach(
          (layer) => {
            const toRemove = layer.graphics
              .filter((g) => g.attributes?.uuid === uuid)
              .toArray();
            layer.graphics.removeMany(toRemove);
          },
        );

        clearLastSelectedGraphic([
          loadedObjectsLayer,
          linesLayer,
          tempLayer,
          polygonsLayer,
        ]);

        dispatch(
          setGeometries({
            newGeometry: ConvertToPolygons(updatedData),
            stringGeometry: ConvertPolygonsToCoordinatesTable(updatedData),
          }),
        );
      }

      if (zoomIn.match(action)) {
        viewer.zoom += 1;
      }
      if (zoomOut.match(action)) {
        viewer.zoom -= 1;
      }

      if (setLayerVisibilityByEstateEn.match(action)) {
        const { layers } = store.getState().map;

        const dataLayersToFilterByEstate = [
          LayersDataTypes.Plots,
          LayersDataTypes.InvestmentOpportunities,
          LayersDataTypes.CityBoundary,
        ];

        const { estateIds } = action.payload;

        viewer.map.layers.forEach((layer) => {
          const foundLayer = layers.find((l) => l.name === layer.id);

          if (
            foundLayer &&
            dataLayersToFilterByEstate.includes(foundLayer.dataType)
          ) {
            const featureLayer = layer as FeatureLayer;

            const filteredQuery = (foundLayer.style2D?.query || "1=1").replace(
              " and LandUse <> 3",
              "",
            );

            featureLayer.definitionExpression = buildDefinitionExpression({
              estateIds,
              baseQuery: filteredQuery,
            });
          }
        });

        return;
      }

      if (setConvertedFeatures.match(action)) {
        const convertedGeometry = store.getState().map.convertedGeometry;
        addConvertedFeaturesToLayer(convertedGeometry, loadedObjectsLayer);
        return next(action);
      }

      if (clearSelectedGraphic.match(action)) {
        clearSelection();
      }

      if (getLayers.fulfilled.match(action)) {
        next(action);
        AddLayers(viewer);
        return;
      }

      if (polylineOff.match(action)) {
        finishDrawingProcess(viewer);
        stopMeasuring(viewer);
      }
      if (polylineOn.match(action)) {
        startDrawingProcess(viewer);
      }

      if (extentEstateEn.match(action)) {
        const { estateEn, highlightFeature = false } = action.payload;
        handleExtentEstate(
          estateEn,
          store,
          viewer,
          false,
          true,
          highlightFeature,
        );
      }

      next(action);
    };
  };
};
